import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import type { HistoricalJob, DateRange, PricingSetting } from '../types';
import { fetchHistoricalJobs, exportJobs, rerunJob, deleteHistoricalJob } from '../hooks/useJobs';
import { fetchPricingSettings, calculateCost, formatCost } from '../hooks/useSettings';
import { DateRangePicker } from './DateRangePicker';
import { ExportButton } from './ExportButton';
import { ImagesModal } from './ImagesModal';
import { createLogger } from '../utils/logger';

const log = createLogger('HistoricalTable');

function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  isLoading,
  variant = 'danger',
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
  variant?: 'danger' | 'primary';
}) {
  if (!isOpen) return null;

  const buttonClass = variant === 'danger'
    ? 'bg-[#F0544F] hover:bg-[#F0544F]/90'
    : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:opacity-95';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onCancel}
      />
      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white ${buttonClass} rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center gap-2`}
          >
            {isLoading && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const columnHelper = createColumnHelper<HistoricalJob & { cost?: number; onRerun?: (jobId: string) => void; onDelete?: (jobId: string, scanId: string) => void }>();

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs.toFixed(1)}s`;
}

function StatusBadge({ status }: { status: string }) {
  const baseClasses = 'px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5';

  if (status === 'completed') {
    return (
      <span className={`${baseClasses} status-completed`}>
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        <span className="status-text">Completed</span>
      </span>
    );
  }

  if (status === 'cancelled') {
    return (
      <span className={`${baseClasses} status-cancelled`}>
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span className="status-text">Cancelled</span>
      </span>
    );
  }

  return (
    <span className={`${baseClasses} status-failed`}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
      <span className="status-text">Failed</span>
    </span>
  );
}

function createColumns(
  onRerun: (jobId: string, scanId: string) => void,
  onDelete: (jobId: string, scanId: string) => void,
  onViewImages: (jobId: string, scanId: string) => void,
  onCopyResult: (jobId: string, result: Record<string, unknown> | null) => void,
  rerunningJobId: string | null,
  copiedJobId: string | null,
) {
  return [
    columnHelper.accessor('scan_id', {
      header: 'Scan ID',
      cell: (info) => {
        const value = info.getValue();
        return (
          <span
            className="font-mono text-[11px] text-indigo-600 font-medium whitespace-nowrap"
            title={value}
          >
            <span className="scan-id-short">{value?.substring(0, 8)}</span>
            <span className="scan-id-full">{value}</span>
          </span>
        );
      },
    }),
    columnHelper.accessor('uvcamp_link', {
      header: 'UVCamp',
      cell: (info) => {
        const link = info.getValue();
        if (!link) return <span className="text-slate-400">-</span>;
        return (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
          >
            Link
          </a>
        );
      },
    }),
    columnHelper.accessor('source', {
      header: 'Source',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span className="text-slate-400">-</span>;
        if (value === 'manual') {
          return (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-50 text-cyan-600">
              Manual
            </span>
          );
        }
        if (value === 'uvcamp') {
          return (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600">
              UVCamp
            </span>
          );
        }
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
            {value}
          </span>
        );
      },
    }),
    columnHelper.accessor('organization_id', {
      header: 'Organization',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span className="text-slate-400">-</span>;
        const truncated = value.length > 12 ? `${value.substring(0, 12)}...` : value;
        return (
          <span className="font-mono text-xs text-slate-500" title={value}>
            {truncated}
          </span>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => <StatusBadge status={info.getValue()} />,
    }),
    columnHelper.accessor('completed_at', {
      header: 'Completed At',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return '-';
        try {
          return (
            <span className="text-slate-700">
              {format(new Date(value), 'MMM d, yyyy HH:mm:ss')}
            </span>
          );
        } catch {
          return '-';
        }
      },
    }),
    columnHelper.accessor('duration_seconds', {
      header: 'Duration',
      cell: (info) => {
        const value = info.getValue();
        const status = info.row.original.status;

        return (
          <span className={`font-mono font-medium ${status === 'completed' ? 'text-[#70B77E]' : 'text-[#F0544F]'}`}>
            {formatDuration(value)}
          </span>
        );
      },
    }),
    columnHelper.accessor('cost', {
      header: 'Cost',
      cell: (info) => {
        const value = info.getValue();
        if (value === undefined || value === null) {
          return <span className="text-slate-400">-</span>;
        }
        return (
          <span className="font-mono text-sm text-slate-700 font-medium">
            {formatCost(value)}
          </span>
        );
      },
    }),
    columnHelper.accessor('error', {
      header: 'Error',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span className="text-slate-400">-</span>;

        const truncated = value.length > 50 ? `${value.substring(0, 50)}...` : value;

        return (
          <span className="text-[#F0544F] text-sm" title={value}>
            {truncated}
          </span>
        );
      },
    }),
    columnHelper.accessor('job_id', {
      header: 'Job ID',
      cell: (info) => (
        <span className="font-mono text-xs text-slate-400">
          {info.getValue().substring(0, 8)}...
        </span>
      ),
    }),
    columnHelper.accessor('batch_id', {
      header: 'Batch ID',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span className="text-slate-400">-</span>;
        return (
          <span className="font-mono text-xs text-slate-400" title={value}>
            {value.substring(0, 8)}...
          </span>
        );
      },
    }),
    columnHelper.accessor('service_version', {
      header: 'BE Version',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span className="text-slate-400">-</span>;
        return (
          <span
            className="font-mono text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700"
            title={value}
          >
            {value}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const jobId = info.row.original.job_id;
        const scanId = info.row.original.scan_id;
        const status = info.row.original.status;
        const result = info.row.original.result;
        const isRerunning = rerunningJobId === jobId;
        const hasResult = result !== null && result !== undefined;
        const isCopied = copiedJobId === jobId;
        
        return (
          <div className="flex items-center gap-2">
            {status === 'completed' && (
              <button
                onClick={() => onViewImages(jobId, scanId)}
                className="p-1.5 text-xs font-medium text-[#70B77E] bg-[#70B77E]/10 hover:bg-[#70B77E]/20 rounded-md transition-colors flex items-center justify-center"
                title="View generated images"
                aria-label="View generated images"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            )}
            <button
              onClick={() => onCopyResult(jobId, result)}
              disabled={!hasResult}
              className="p-1.5 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              title={hasResult ? (isCopied ? 'Copied!' : 'Copy scan result JSON to clipboard') : 'No result available'}
              aria-label={hasResult ? (isCopied ? 'Copied' : 'Copy scan result JSON to clipboard') : 'No result available'}
            >
              {isCopied ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => onRerun(jobId, scanId)}
              disabled={isRerunning}
              className="p-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center"
              title="Re-run this scan"
              aria-label="Re-run this scan"
            >
              {isRerunning ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button>
            <button
              onClick={() => onDelete(jobId, scanId)}
              className="p-1.5 text-xs font-medium text-[#F0544F] bg-[#F0544F]/10 hover:bg-[#F0544F]/20 rounded-md transition-colors flex items-center justify-center"
              title="Delete this scan"
              aria-label="Delete this scan"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        );
      },
    }),
  ];
}

export function HistoricalTable() {
  const [jobs, setJobs] = useState<HistoricalJob[]>([]);
  // Tracks which scan cards are expanded on mobile. Cards are collapsed
  // by default (showing only Scan ID / Status / Cost); tapping a card
  // toggles its full detail. Keyed by react-table row id.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pricingSettings, setPricingSettings] = useState<PricingSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>({ from: null, to: null });
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [scanIdFilter, setScanIdFilter] = useState<string>('');
  const [orgFilter, setOrgFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  
  // Re-run and delete state
  const [rerunningJobId, setRerunningJobId] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ jobId: string; scanId: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [imagesDialog, setImagesDialog] = useState<{ jobId: string; scanId: string } | null>(null);
  const [copiedJobId, setCopiedJobId] = useState<string | null>(null);

  // Collapse every card whenever the underlying data changes (new page,
  // filter, or reload) so the mobile list always starts compact.
  useEffect(() => {
    setExpandedRows(new Set());
  }, [jobs]);

  const toggleRow = useCallback((rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleCopyResult = useCallback(
    async (jobId: string, result: Record<string, unknown> | null) => {
      if (!result) return;
      try {
        await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
        setCopiedJobId(jobId);
        setTimeout(() => {
          setCopiedJobId((curr) => (curr === jobId ? null : curr));
        }, 1500);
      } catch (err) {
        log.error('Failed to copy scan result:', err);
        alert('Failed to copy to clipboard');
      }
    },
    [],
  );

  // Load pricing settings on mount
  useEffect(() => {
    fetchPricingSettings()
      .then(setPricingSettings)
      .catch((err) => log.error('Failed to load pricing settings:', err));
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchHistoricalJobs(
        dateRange, statusFilter, page, pageSize,
        scanIdFilter || null, orgFilter || null,
      );
      setJobs(response.jobs);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (error) {
      log.error('Failed to load history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, statusFilter, page, pageSize, scanIdFilter, orgFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Handle re-run job
  const handleRerun = useCallback(async (jobId: string, _scanId: string) => {
    setRerunningJobId(jobId);
    try {
      await rerunJob(jobId);
      log.info(`Re-ran job ${jobId}`);
      loadData();
    } catch (error) {
      log.error(`Failed to re-run job ${jobId}:`, error);
      alert(error instanceof Error ? error.message : 'Failed to re-run job');
    } finally {
      setRerunningJobId(null);
    }
  }, [loadData]);

  // Handle delete job
  const handleDelete = useCallback(async () => {
    if (!deleteDialog) return;
    
    setIsDeleting(true);
    try {
      await deleteHistoricalJob(deleteDialog.jobId);
      log.info(`Deleted job ${deleteDialog.jobId}`);
      setDeleteDialog(null);
      loadData();
    } catch (error) {
      log.error(`Failed to delete job ${deleteDialog.jobId}:`, error);
      alert(error instanceof Error ? error.message : 'Failed to delete job');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteDialog, loadData]);

  // Calculate costs for each job
  const jobsWithCosts = useMemo(() => {
    return jobs.map(job => ({
      ...job,
      cost: calculateCost(job.fal_usage, job.gemini_usage, pricingSettings),
    }));
  }, [jobs, pricingSettings]);
  
  const columns = useMemo(
    () => createColumns(
      handleRerun,
      (jobId, scanId) => setDeleteDialog({ jobId, scanId }),
      (jobId, scanId) => setImagesDialog({ jobId, scanId }),
      handleCopyResult,
      rerunningJobId,
      copiedJobId,
    ),
    [handleRerun, handleCopyResult, rerunningJobId, copiedJobId]
  );

  const handleExport = async (format: 'csv' | 'json') => {
    setIsExporting(true);
    try {
      await exportJobs(dateRange, statusFilter, format);
    } catch (error) {
      log.error(`Export failed (format=${format}):`, error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    setPage(1); // Reset to first page on filter change
  };

  const handleStatusChange = (status: string | null) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleScanIdChange = (value: string) => {
    setScanIdFilter(value);
    setPage(1);
  };

  const handleOrgChange = (value: string) => {
    setOrgFilter(value);
    setPage(1);
  };

  const table = useReactTable({
    data: jobsWithCosts,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden transition-all duration-200">
      {/* Header with filters */}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Scan History</h2>
            <p className="text-sm text-slate-500">
              {total.toLocaleString()} total scan{total !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Status filter (mobile only, sits next to title) */}
          <div className="flex items-center gap-2 shrink-0 sm:hidden">
            <select
              value={statusFilter || ''}
              onChange={(e) => handleStatusChange(e.target.value || null)}
              className="input-pill"
              title="Status filter"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 sm:gap-3 flex-nowrap sm:flex-wrap overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
            <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />

            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <label className="text-xs text-slate-500 hidden sm:block">Status:</label>
              <select
                value={statusFilter || ''}
                onChange={(e) => handleStatusChange(e.target.value || null)}
                className="input-pill"
                title="Status filter"
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-nowrap sm:flex-wrap">
            <input
              type="text"
              value={scanIdFilter}
              onChange={(e) => handleScanIdChange(e.target.value)}
              placeholder="Search scan ID..."
              title="Search scan ID"
              className="input-pill flex-1 w-0 min-w-0 sm:w-44 sm:flex-none"
            />

            <input
              type="text"
              value={orgFilter}
              onChange={(e) => handleOrgChange(e.target.value)}
              placeholder="Search org..."
              title="Search organization"
              className="input-pill flex-1 w-0 min-w-0 sm:w-44 sm:flex-none"
            />

            <button
              onClick={loadData}
              title="Apply Filters"
              className="btn-secondary px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="hidden sm:inline">Apply Filters</span>
            </button>

            <div className="shrink-0">
              <ExportButton onExport={handleExport} isLoading={isExporting} />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="inline-flex items-center gap-3 text-slate-500">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading...
          </div>
        </div>
      ) : jobs.length === 0 ? (
        <div className="p-12 text-center">
          <div className="inline-flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-slate-900 font-medium">No historical data</p>
              <p className="text-sm text-slate-500">Try adjusting your date range or filters</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table mobile-collapsible summary-inline">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-5 py-3 text-left"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-slate-50 transition-colors"
                  data-collapsed={!expandedRows.has(row.id)}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, a, input, select')) return;
                    toggleRow(row.id);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-5 py-3"
                      data-label={typeof cell.column.columnDef.header === 'string' ? cell.column.columnDef.header : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-center md:justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap justify-center md:justify-start">
          <div className="text-xs text-slate-500">
            {total > 0
              ? `Showing ${((page - 1) * pageSize) + 1} - ${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`
              : 'No results'}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Rows:</span>
            {[20, 50, 100].map((size) => (
              <button
                key={size}
                onClick={() => handlePageSizeChange(size)}
                className={`px-2 py-0.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  pageSize === size
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            title="First page"
            aria-label="First page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'<<'}
          </button>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            title="Previous page"
            aria-label="Previous page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'<'}
          </button>
          <span className="px-1.5 py-1 text-xs text-slate-600 whitespace-nowrap">
            {page}/{totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            title="Next page"
            aria-label="Next page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'>'}
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            title="Last page"
            aria-label="Last page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'>>'}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog !== null}
        title="Delete Scan"
        message={`Are you sure you want to delete scan "${deleteDialog?.scanId}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog(null)}
        isLoading={isDeleting}
        variant="danger"
      />

      {/* Generated Images Modal */}
      <ImagesModal
        isOpen={imagesDialog !== null}
        jobId={imagesDialog?.jobId ?? null}
        scanId={imagesDialog?.scanId ?? null}
        onClose={() => setImagesDialog(null)}
      />
    </div>
  );
}
